import i18n from "../i18n";

// Capability matrix mirror of the backend (core/capabilities.py). Texts live in locales/admin.*.json
// (admin.capabilities.items.<key with "." -> "_">.label / .help).
export const DELEGATABLE_CAPABILITIES = [
  "edge.claim", "edge.add_manual", "edge.transfer", "edge.cloud_transfer", "edge.tier.change",
  "license.assign", "license.revoke", "ota.publish", "support.remote_access", "security.sbom",
].map((key) => ({ key }));

const capId = (key) => key.replace(/\./g, "_");
export const capabilityLabel = (key) => i18n.t(`admin.capabilities.items.${capId(key)}.label`, { defaultValue: key });
export const capabilityHelp = (key) => i18n.t(`admin.capabilities.items.${capId(key)}.help`, { defaultValue: "" });

// Effective capability of the signed-in user: /auth/me carries `capabilities` (superadmin gets all of them);
// the global_role check only covers older backends that do not send the field yet.
export function hasCap(me, cap) {
  if (!me) return false;
  if (Array.isArray(me.capabilities)) return me.capabilities.includes(cap);
  return me.global_role === "superadmin";
}

// Backend error codes of the capability endpoints -> user-facing text
export function apiErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail !== "string") return fallback;
  if (detail.startsWith("capability_required:")) return i18n.t("admin.errors.capabilityRequired", { capability: capabilityLabel(detail.slice("capability_required:".length)) });
  if (detail.startsWith("not_delegatable:")) return i18n.t("admin.errors.notDelegatable", { capability: detail.slice("not_delegatable:".length) });
  if (detail === "license_required") return i18n.t("admin.errors.licenseRequired");
  if (detail === "Not allowed") return i18n.t("admin.errors.notAllowed");
  return detail;
}

// Error codes of the machine SBOM endpoints (detail is an object {code, ...}) -> text in the selected language
export function sbomErrorMessage(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return apiErrorMessage(err, fallback);
  const t = (key, opts) => i18n.t(`sbom.errors.${key}`, opts);
  const hours = Math.max(1, Math.ceil((d?.retry_after_seconds || 0) / 3600));
  switch (d?.code) {
    case "sbom_machine_quota_exceeded":
      return t("quota", { used: d.used, limit: d.limit, burnDays: d.burn_days });
    case "sbom_replace_cooldown":
      return t("cooldown", { hours });
    case "sbom_scan_queue_full":
      return t("queueFull");
    case "sbom_upload_rate_limit":
      return t("uploadRate", { perHour: d.per_hour });
    case "sbom_too_large":
      return t("tooLarge", { mb: Math.round((d.max_bytes || 0) / 1048576) });
    case "sbom_too_many_components":
      return t("tooMany", { max: d.max_components });
    case "sbom_empty":
      return t("empty");
    case "sbom_invalid": {
      const lines = (d.rows || []).map((r) => r.line).join(", ");
      if (lines) return t("csvLines", { count: d.error_count, lines });
      return d.message ? t("invalidWithMessage", { message: d.message }) : t("invalid");
    }
    case "sbom_suggest_rate_limit":
      return t("suggestRate", { perHour: d.per_hour });
    case "nvd_unavailable":
      return t("nvd");
    case "cpe_invalid":
      return t("cpeInvalid");
    case "component_not_found":
      return t("componentNotFound");
    case "mapping_not_found":
      return t("mappingNotFound");
    case "sbom_scan_in_progress":
      return t("scanInProgress");
    case "sbom_rescan_too_soon":
      return t("rescanSoon");
    default:
      return fallback;
  }
}

// Product profile of the signed-in user's tenant: /auth/me carries `tenant_products` (superadmin: all of them).
// An older backend that does not send it means "everything", the behaviour before product profiles existed.
export const PRODUCTS = ["iot", "cra"];

export function hasProduct(me, product) {
  if (!me) return false;
  if (Array.isArray(me.tenant_products)) return me.tenant_products.includes(product);
  return true;
}
